import { httpsCallable } from 'firebase/functions';
import { functions } from './firebaseFunctions';

/**
 * Details for a single file in a batch request
 */
export interface FileDetail {
    /** ID of the attachment to create sharing for */
    fileId: string;
    /** ID of the version to share */
    versionId: string;
    /** Optional path to the artifact referencing the attachment */
    artifactPath?: string;
}

/**
 * Interface for the request to create attachment share links
 */
export interface CreateAttachmentLinkRequest {
    /** Array of files to create sharing links for */
    files: FileDetail[];
    /** Expiration time in milliseconds since epoch (applies to all files) */
    expirationTime?: number;
}

/**
 * Result for a single file sharing operation
 */
export interface CreateAttachmentLinkFileResult {
    /** ID of the file that was processed */
    fileId: string;
    /** ID of the version that was processed */
    versionId: string;
    /** Expiration time for the link (if successful) */
    expirationTime?: number;
    /** URL for the shared file (if successful) */
    shareUrl?: string;
    /** Error message (if operation failed for this file) */
    error?: string;
}

/**
 * Complete result of a batch share link creation operation
 */
export interface CreateAttachmentLinkBatchResult {
    /** Overall error if the batch operation itself failed */
    error?: string;
    /** Individual results for each file, keyed by "fileId:versionId" */
    results: Record<string, CreateAttachmentLinkFileResult>;
}

/**
 * Creates shareable links for multiple attachment versions using Firebase Storage download URLs
 * 
 * @param files - Array of file details (fileId, versionId, optional artifactPath) to create sharing for
 * @param expirationTime - Optional expiration time in milliseconds since epoch (applies to all files)
 * @returns Promise resolving to the batch creation result with individual file results
 */
export const createAttachmentLinks = async (
    files: FileDetail[],
    expirationTime?: number
): Promise<CreateAttachmentLinkBatchResult> => {
    const createAttachmentLinkFn = httpsCallable<CreateAttachmentLinkRequest, CreateAttachmentLinkBatchResult>(
        functions, 
        'createAttachmentLink'
    );
    
    const response = await createAttachmentLinkFn({
        files,
        expirationTime
    });
    
    return response.data;
};

/**
 * Creates a shareable link for a single attachment version using Firebase Storage download URLs
 * 
 * @param fileId - ID of the attachment to create sharing for
 * @param versionId - ID of the version to manage
 * @param artifactPath - Optional path to the artifact referencing the attachment
 * @param expirationTime - Optional expiration time in milliseconds since epoch
 * @returns Promise resolving to the creation result with download URL
 */
export const createAttachmentLink = async (
    fileId: string, 
    versionId: string, 
    artifactPath?: string,
    expirationTime?: number
): Promise<CreateAttachmentLinkFileResult> => {
    const result = await createAttachmentLinks(
        [{ fileId, versionId, artifactPath }],
        expirationTime
    );
    
    if (result.error) {
        return {
            fileId,
            versionId,
            error: result.error
        };
    }
    
    const fileResult = result.results[`${fileId}:${versionId}`];
    
    if (!fileResult) {
        return {
            fileId,
            versionId,
            error: 'No result returned for this file'
        };
    }
    
    return fileResult;
}; 